import {
  ActionSchema,
  DynamicNumberSchema,
  DynamicStringSchema,
} from '@a2ui/web_core/v0_9';
import { createComponentImplementation } from '@a2ui/react/v0_9';
import { defineA2uiComponentSchema } from '../../a2ui-schema';
import { ArtifactRow, type ArtifactRowStatus } from './ArtifactRow';

interface ResolvedProps {
  readonly filename: string;
  readonly downloadUrl: string;
  readonly sizeLabel?: string;
  readonly summary?: string;
  /** Widened to string — bindings resolve to strings, narrowed on the way in. */
  readonly status?: string;
  readonly progress?: number;
  readonly error?: string;
  readonly onPreview?: () => void;
  readonly onDownload?: () => void;
}

const VALID_STATUSES: readonly string[] = ['ready', 'generating', 'failed'];

function asStatus(value: string | undefined): ArtifactRowStatus {
  return VALID_STATUSES.includes(value ?? '')
    ? (value as ArtifactRowStatus)
    : 'ready';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const schema = defineA2uiComponentSchema({
  filename: DynamicStringSchema,
  downloadUrl: DynamicStringSchema,
  sizeLabel: DynamicStringSchema,
  summary: DynamicStringSchema,
  status: DynamicStringSchema,
  progress: DynamicNumberSchema,
  error: DynamicStringSchema,
  onPreview: ActionSchema,
  onDownload: ActionSchema,
}) as any;

export const ArtifactRowImpl = createComponentImplementation(
  { name: 'ArtifactRow', schema },
  // Both the authed download and the preview panel are host-specific, so they
  // ride the action channel: the agent binds `onDownload` / `onPreview` (with the
  // download URL + filename in context) and the console's onA2UIAction performs
  // the work. Same shape as FileArtifactCard's onDownload.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ({
    props,
  }: Readonly<{ props: ResolvedProps; buildChild: any; context: any }>) => (
    <ArtifactRow
      filename={props.filename ?? ''}
      downloadUrl={props.downloadUrl ?? ''}
      sizeLabel={props.sizeLabel}
      summary={props.summary}
      status={asStatus(props.status)}
      progress={props.progress}
      error={props.error}
      onPreview={props.onPreview}
      onDownload={props.onDownload}
    />
  )
);
